import { useEffect, useMemo, useState } from 'react';
import {
  api,
  type AlertSuggestion,
  type Category,
  type Hunt,
  type MeasurePreset,
  type Radar,
} from '../api';

/**
 * Alert editor.
 *
 * Two things this screen has to get right that the radar editor does not.
 *
 * The first is that a price ceiling on a consumable is not a number anybody can
 * sanity-check. "Red Bull under $1.50 a can" sounds obviously reasonable and
 * might match nothing for a month, or forty times a day, and those two failures
 * look identical at the moment you type it. So the preview is not a nicety here
 * — it is the only feedback loop, and it runs on every change.
 *
 * The second is that a category alert quietly matches words the user never
 * typed. Asking for "soda" and being pushed a Dr Pepper deal is the feature
 * working, and it reads as a bug unless the screen says up front that it will
 * happen. Hence the expanded terms being shown, not hidden behind a disclosure.
 */
export function AlertPanel({
  hunt,
  categories,
  radars,
  suggestion,
  onClose,
  onSaved,
}: {
  hunt: Hunt | null;
  categories: Category[];
  radars: Radar[];
  /** Pre-fill from a starter alert. Ignored when editing an existing one. */
  suggestion?: AlertSuggestion | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const editing = hunt !== null;
  const seed = editing ? null : (suggestion ?? null);

  const [name, setName] = useState(hunt?.name ?? seed?.name ?? '');
  const [query, setQuery] = useState(hunt?.query ?? seed?.query ?? '');
  const [excludeTerms, setExcludeTerms] = useState(
    (hunt?.excludeTerms ?? seed?.excludeTerms ?? []).join(', '),
  );
  const [brands, setBrands] = useState((hunt?.brands ?? seed?.brands ?? []).join(', '));
  const [cats, setCats] = useState<string[]>(hunt?.categories ?? seed?.categories ?? []);
  const [subs, setSubs] = useState<string[]>(hunt?.subcategories ?? seed?.subcategories ?? []);

  const [includeNearby, setIncludeNearby] = useState(hunt?.includeNearby ?? true);
  const [includeOnline, setIncludeOnline] = useState(hunt?.includeOnline ?? true);
  const [includePinnedStores, setIncludePinnedStores] = useState(
    hunt?.includePinnedStores ?? true,
  );
  const [radarId, setRadarId] = useState<string | null>(hunt?.radarId ?? null);

  const [maxPrice, setMaxPrice] = useState(dollars(hunt?.maxPriceCents ?? seed?.maxPriceCents));
  const [maxUnitPrice, setMaxUnitPrice] = useState(
    dollars(hunt?.maxUnitPriceCents ?? seed?.maxUnitPriceCents),
  );
  const [measurePresetId, setMeasurePresetId] = useState<string>(hunt?.measurePreset ?? '');
  const [measureAmount, setMeasureAmount] = useState(dollars(hunt?.measurePresetCents));
  const [minDiscount, setMinDiscount] = useState(
    numeric(hunt?.minDiscountPct ?? seed?.minDiscountPct),
  );
  const [minUnitCount, setMinUnitCount] = useState(
    numeric(hunt?.minUnitCount ?? seed?.minUnitCount),
  );
  const [requireInStock, setRequireInStock] = useState(hunt?.requireInStock ?? false);
  const [freeOnly, setFreeOnly] = useState(hunt?.freeOnly ?? false);

  const [notify, setNotify] = useState(hunt?.notify ?? true);
  const [maxPerDay, setMaxPerDay] = useState(
    numeric(hunt?.maxAlertsPerDay ?? seed?.maxAlertsPerDay),
  );
  const [active, setActive] = useState(hunt?.active ?? true);

  const [measures, setMeasures] = useState<MeasurePreset[]>([]);
  const [preview, setPreview] = useState<Awaited<ReturnType<typeof api.previewHunt>> | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [advanced, setAdvanced] = useState(false);

  useEffect(() => {
    api
      .alertSuggestions()
      .then((r) => setMeasures(r.measures))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // A seeded per-measure ceiling arrives in base units; turn it back into the
  // unit a person would have typed so the field is editable rather than showing
  // "0.4 cents per millilitre".
  useEffect(() => {
    if (!seed?.maxMeasurePriceCents || !seed.measureDim || !measures.length) return;
    if (measurePresetId) return;
    const preset =
      measures.find((m) => m.dim === seed.measureDim && m.perBase >= 100) ??
      measures.find((m) => m.dim === seed.measureDim);
    if (!preset) return;
    setMeasurePresetId(preset.id);
    setMeasureAmount(((seed.maxMeasurePriceCents * preset.perBase) / 100).toFixed(2));
  }, [seed, measures, measurePresetId]);

  const body = useMemo(
    () => ({
      name: name.trim() || query.trim(),
      query: query.trim(),
      excludeTerms: splitList(excludeTerms),
      brands: splitList(brands),
      categories: cats,
      subcategories: subs,
      radarId,
      includeNearby,
      includeOnline,
      includePinnedStores,
      freeOnly,
      maxPriceCents: cents(maxPrice),
      maxUnitPriceCents: cents(maxUnitPrice),
      // The server owns the conversion to base units; sending the preset and
      // the number the user typed keeps the factor in exactly one place.
      measurePreset: measurePresetId || null,
      measurePresetCents: measurePresetId ? cents(measureAmount) : null,
      minDiscountPct: int(minDiscount),
      minUnitCount: int(minUnitCount),
      requireInStock,
      notify,
      maxAlertsPerDay: int(maxPerDay),
      active,
    }),
    [
      name, query, excludeTerms, brands, cats, subs, radarId,
      includeNearby, includeOnline, includePinnedStores, freeOnly,
      maxPrice, maxUnitPrice, measurePresetId, measureAmount,
      minDiscount, minUnitCount, requireInStock, notify, maxPerDay, active,
    ],
  );

  // Debounced: every keystroke in the query box would otherwise be a real query.
  useEffect(() => {
    if (body.query.length < 2) {
      setPreview(null);
      return;
    }
    const timer = setTimeout(() => {
      api.previewHunt(body).then(setPreview).catch(() => setPreview(null));
    }, 450);
    return () => clearTimeout(timer);
  }, [body]);

  const subcategoriesFor = useMemo(
    () => categories.filter((c) => cats.length === 0 || cats.includes(c.id)),
    [categories, cats],
  );

  const measureUnit = measures.find((m) => m.id === measurePresetId);

  async function save() {
    if (body.query.length < 2) {
      setError('Say what to watch for — "soda", "red bull", "chips".');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (editing) await api.updateHunt(hunt.id, body);
      else await api.createHunt(body);
      onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save alert');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!hunt) return;
    setBusy(true);
    try {
      await api.deleteHunt(hunt.id);
      onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not delete alert');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="scrim" onClick={onClose} />
      <aside className="panel" role="dialog" aria-modal="true" aria-label="Alert settings">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span className="tag-cat" style={{ color: 'var(--field-text-dim)' }}>
            {editing ? 'Alert' : 'New alert'}
          </span>
          <button className="panel-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        {error && <p className="error">{error}</p>}

        <label className="form-label" htmlFor="alert-query">
          Watch for
        </label>
        <input
          id="alert-query"
          className="input"
          value={query}
          placeholder="soda, red bull, chips, lay's…"
          onChange={(e) => setQuery(e.target.value)}
          maxLength={80}
        />

        {/*
          The expanded terms, in plain sight. A "soda" alert firing on a Dr
          Pepper deal is correct and reads as a bug if this list is hidden —
          and someone who genuinely only wants Coke can see immediately that
          they should be typing "coke" instead.
        */}
        {preview && preview.matchTerms.length > 1 && (
          <p className="alert-terms">
            Also matches: {preview.matchTerms.filter((t) => t !== query.trim().toLowerCase()).slice(0, 12).join(' · ')}
          </p>
        )}

        <label className="form-label" htmlFor="alert-name">
          Call it
        </label>
        <input
          id="alert-name"
          className="input"
          value={name}
          placeholder={query || 'Alert name'}
          onChange={(e) => setName(e.target.value)}
          maxLength={60}
        />

        <label className="form-label">Price</label>
        <div className="alert-grid">
          <Money label="Per item" value={maxUnitPrice} onChange={setMaxUnitPrice} placeholder="1.50" />
          <div>
            <span className="alert-mini">Per measure</span>
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                className="input"
                inputMode="decimal"
                placeholder="4.00"
                value={measureAmount}
                aria-label="Per measure ceiling"
                onChange={(e) => setMeasureAmount(e.target.value)}
              />
              <select
                className="input"
                value={measurePresetId}
                aria-label="Measure unit"
                onChange={(e) => setMeasurePresetId(e.target.value)}
              >
                <option value="">unit…</option>
                {measures.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <Money label="Total" value={maxPrice} onChange={setMaxPrice} placeholder="20.00" />
          <Count label="% off, at least" value={minDiscount} onChange={setMinDiscount} placeholder="30" />
        </div>
        <p className="alert-hint">
          {maxUnitPrice || (measureAmount && measureUnit)
            ? 'Pack size is worked out for you — a 24-pack and a single can are compared on the same terms.'
            : 'A per-item or per-measure ceiling is what makes an alert on a consumable useful. A total price alone matches a single can as readily as a case.'}
        </p>

        <label className="form-label">Where to look</label>
        <Toggle checked={includeOnline} onChange={setIncludeOnline} label="Online deals" />
        <Toggle
          checked={includePinnedStores}
          onChange={setIncludePinnedStores}
          label="Stores I've pinned"
        />
        <Toggle checked={includeNearby} onChange={setIncludeNearby} label="Anywhere in my radars" />
        {includeNearby && radars.length > 1 && (
          <select
            className="input"
            style={{ marginTop: 6 }}
            value={radarId ?? ''}
            aria-label="Which radar"
            onChange={(e) => setRadarId(e.target.value || null)}
          >
            <option value="">Any of my radars</option>
            {radars.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name} · {r.radiusDisplay}
              </option>
            ))}
          </select>
        )}
        {!includeOnline && !includePinnedStores && !includeNearby && (
          <p className="error">
            With all three off this alert can never match anything.
          </p>
        )}

        <label className="form-label">Categories</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {categories.map((c) => {
            const on = cats.includes(c.id);
            return (
              <button
                key={c.id}
                className="chip"
                aria-pressed={on}
                onClick={() => {
                  const next = on ? cats.filter((x) => x !== c.id) : [...cats, c.id];
                  setCats(next);
                  // Subcategories belonging to a category that just went away
                  // would silently keep filtering. Drop them with it.
                  if (on) {
                    const gone = new Set(c.subcategories.map((s) => s.id));
                    setSubs(subs.filter((s) => !gone.has(s)));
                  }
                }}
              >
                {c.emoji} {c.label}
              </button>
            );
          })}
        </div>

        {cats.length > 0 && (
          <>
            <label className="form-label">Narrow it down</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {subcategoriesFor.flatMap((c) =>
                c.subcategories.map((s) => {
                  const on = subs.includes(s.id);
                  return (
                    <button
                      key={s.id}
                      className="chip"
                      aria-pressed={on}
                      onClick={() => setSubs(on ? subs.filter((x) => x !== s.id) : [...subs, s.id])}
                    >
                      {s.label}
                    </button>
                  );
                }),
              )}
            </div>
          </>
        )}

        <label className="form-label">Alerts</label>
        <Toggle checked={notify} onChange={setNotify} label="Push me when something matches" />
        <Toggle checked={active} onChange={setActive} label="Alert is on" />
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
          <span className="alert-mini" style={{ flex: 1 }}>
            At most, per day
          </span>
          <input
            className="input"
            style={{ width: 90 }}
            inputMode="numeric"
            placeholder="no limit"
            value={maxPerDay}
            aria-label="Maximum alerts per day"
            onChange={(e) => setMaxPerDay(e.target.value)}
          />
        </div>

        <button className="link" style={{ marginTop: 14 }} onClick={() => setAdvanced(!advanced)}>
          {advanced ? 'Fewer options' : 'More options'}
        </button>

        {advanced && (
          <>
            <label className="form-label" htmlFor="alert-brands">
              Only these brands
            </label>
            <input
              id="alert-brands"
              className="input"
              value={brands}
              placeholder="lay's, doritos"
              onChange={(e) => setBrands(e.target.value)}
            />
            <p className="alert-hint">
              Checked against the deal's brand, not its title — narrower than a keyword.
            </p>

            <label className="form-label" htmlFor="alert-exclude">
              Never match
            </label>
            <input
              id="alert-exclude"
              className="input"
              value={excludeTerms}
              placeholder="diet, zero sugar"
              onChange={(e) => setExcludeTerms(e.target.value)}
            />

            <div className="alert-grid" style={{ marginTop: 12 }}>
              <Count
                label="Pack of at least"
                value={minUnitCount}
                onChange={setMinUnitCount}
                placeholder="12"
              />
            </div>

            <Toggle checked={requireInStock} onChange={setRequireInStock} label="Skip sold-out deals" />
            <Toggle checked={freeOnly} onChange={setFreeOnly} label="Free only" />
          </>
        )}

        {/*
          The whole reason this panel has a preview. A threshold that is merely
          strict and one that is wrong produce the same screen — zero matches —
          and the difference only shows up a week later as silence.
        */}
        <div className="alert-preview">
          {preview === null ? (
            <span>Type what to watch for to see how often this would fire.</span>
          ) : preview.matchesLast7Days === 0 ? (
            <>
              <strong>Nothing matched in the last week.</strong>
              <span>
                That may be right for a rare deal, or the ceiling may be lower than anything on
                the shelf. Loosen it and watch this number move.
              </span>
            </>
          ) : (
            <>
              <strong>
                {preview.matchesLast7Days} in the last week · about {preview.cappedPerDay} a day
                {preview.cappedPerDay < preview.estimatedPerDay && ' (capped)'}
              </strong>
              <span>{preview.examples.slice(0, 3).join(' · ')}</span>
            </>
          )}
        </div>

        <div className="btn-row">
          <button className="btn" onClick={save} disabled={busy}>
            {editing ? 'Save alert' : 'Create alert'}
          </button>
          <button className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          {editing && (
            <button className="btn btn-ghost" onClick={remove} disabled={busy}>
              Delete
            </button>
          )}
        </div>
      </aside>
    </>
  );
}

function Money({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <div>
      <span className="alert-mini">{label}</span>
      <input
        className="input"
        inputMode="decimal"
        placeholder={placeholder}
        value={value}
        aria-label={label}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function Count({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <div>
      <span className="alert-mini">{label}</span>
      <input
        className="input"
        inputMode="numeric"
        placeholder={placeholder}
        value={value}
        aria-label={label}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <label style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '7px 0', fontSize: 13 }}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );
}

// An empty field means "no ceiling", which is different from zero — "free
// only" is its own switch. So these all return null rather than 0 on blank.
function cents(value: string): number | null {
  const n = Number.parseFloat(value.replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) ? Math.round(n * 100) : null;
}

function int(value: string): number | null {
  const n = Number.parseInt(value.replace(/[^0-9]/g, ''), 10);
  return Number.isFinite(n) ? n : null;
}

function dollars(cents: number | null | undefined): string {
  return cents === null || cents === undefined ? '' : (cents / 100).toFixed(2);
}

function numeric(value: number | null | undefined): string {
  return value === null || value === undefined ? '' : String(value);
}

function splitList(value: string): string[] {
  return value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}
